import React from 'react';
import { connect } from 'react-redux';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting';
import moment from 'moment';
import _ from 'lodash';

import withChartExport from '../../../components/ChartExporting';
import dataUtils from '../../../util/dataUtils';
import { isWhite, PERCENTILE_LINE_COLORS, PERCENTILE_LABEL_COLORS } from '../../../util/colors';

import { trackChartExport } from '../../../util/mixpanel';
import { escapeHTML } from '../../../util/text';
import { formatWorkDuration, getWorkMinutesPerDay } from '../../../util/dates';

import highchartFactory from '../../../util/highcharts';
import chartUtils from '../../../util/chartUtils';
import { colorMap, PRIMARY_NAVY } from '../../../util/colorMap';
import { WALLBOARD_TEXT_SIZE } from '../../../util/wallboardFrame';
import exportComposition from '../../../util/chartExportComposition';
const highchartUtils = highchartFactory(Highcharts);

const ZONE_OVERFLOW_FILL = '#f8cdd1';
// 3:1 contrast on every zone colour
const DEFAULT_DOT_MARKER = { fillColor: colorMap('default'), lineColor: PRIMARY_NAVY, lineWidth: 1 };

let bottomXAxisConfig = {
  labels: {
    style: {
      color: chartUtils.TREND_LABEL_COLOR,
      fontSize: '12px'
    }
  },
  gridLineColor: '#F5F5F5',
  gridLineWidth: 1,
  gridZIndex: 1,
  categories: []
};

let chartConfig = {
  credits: {
    enabled: false
  },
  chart: {
    backgroundColor: null,
    type: 'scatter',
    zoomType: 'xy',
    spacingBottom: 10,
    spacingTop: 40,
    marginRight: 50,
    marginLeft: 80,
    events: {
      load: e => {
        e.target.options.exporting.sourceHeight = e.target.chartHeight;
        e.target.options.exporting.sourceWidth = e.target.chartWidth;
      }
    }
  },
  title: {
    text: ''
  },
  xAxis: [
    bottomXAxisConfig
  ],
  yAxis: {
    min: 0,
    allowDecimals: false,
    gridLineColor: chartUtils.TREND_GRID_COLOR,
    gridLineWidth: 1,
    gridZIndex: 1,
    title: {
      text: null
    },
    labels: {
      style: {
        color: chartUtils.TREND_LABEL_COLOR,
        fontSize: '12px',
        whiteSpace: 'nowrap'
      }
    },
    plotLines: []
  },
  legend: {
    enabled: false
  },
  plotOptions: {
    series: {
      stickyTracking: false,
      cursor: 'pointer',
      point: {
        events: {}
      }
    },
    scatter: {
      marker: {
        lineColor: '#FFFFFF',
        lineWidth: 2,
        lineZIndex: 2,
        symbol: 'circle',
        radius: 8,
        states: {
          hover: chartUtils.hoverMarker(),
          select: chartUtils.selectedMarker(11)
        }
      },
      states: {
        hover: {
          marker: {
            enabled: false
          }
        }
      }
    }
  },
  tooltip: {
    useHTML: true,
    shadow: false,
    hideDelay: 0,
    borderRadius: 5,
    borderWidth: 0,
    formatter: function() {}
  },
  series: []
};

export class CTAChart extends React.Component {
  constructor(props) {
    super(props);
    this.chartContainer = React.createRef();
    this.state = {};
    this.zones = [];
    this.onRedraw = this.onRedraw.bind(this);
  }

  static defaultProps = {
    onPointClick: function() {}
  };

  calculatePercentiles = (data, index) => {
    const lines = [],
      ticks = [],
      serie = {
        name: 'percentiles',
        type: 'scatter',
        marker: {
          enabled: false
        },
        data: [
          { x: 0, y: null },
          { x: index - 1, y: null }
        ]
      };

    const workMinutesPerDay = getWorkMinutesPerDay(this.props.excludeWeekendOpts, this.props.useWorkingHoursInCycleTimeDays);
    const labelSize = this.props.wallmode ? { fontSize: WALLBOARD_TEXT_SIZE } : highchartUtils.getFontSizeConfig('percentile');

    for (let i = this.props.percentiles.length - 1; i >= 0; i--) {
      const pObj = _.find(data.percentiles, item => item.name == this.props.percentiles[i]);
      if (!pObj) {
        continue;
      }

      const text = `${pObj.name}%`;
      const value = this.props.timeUnit === 'day'
        ? Math.ceil(pObj.rawValue / workMinutesPerDay)
        : Math.ceil(moment.duration(pObj.rawValue, 'minutes').as(this.props.timeUnit));

      serie.data.push({ x: index - 1, y: value });
      lines.push({
        color: PERCENTILE_LINE_COLORS[pObj.name],
        dashStyle: 'Dash',
        width: 1,
        zIndex: 2,
        value,
        label: {
          align: 'right',
          textAlign: 'right',
          className: 'highcharts-plot-line-label-right',
          text,
          x: 80,
          y: 4,
          style: {
            ...labelSize,
            color: PERCENTILE_LABEL_COLORS[pObj.name]
          }
        }
      });

      if (_.findIndex(ticks, { value }) === -1) {
        ticks.push({
          dashStyle: 'line',
          color: 'transparent',
          width: 4,
          zIndex: 2,
          value,
          events: highchartUtils.plotlinesTooltip(text)
        });
      }
    }

    const unit = { day: 'd', week: 'w', month: 'mo' }[this.props.timeUnit] || '';
    const newLines = [];
    _(lines)
      .groupBy('value')
      .forEach(l => {
        const line = l[0];
        const labels = _.map(l, i => {
          return i.label.text;
        });
        line.label.text = `${labels.join(', ')} · ${line.value}${unit}`;
        newLines.push(line);
      });

    chartUtils.fitRightLineLabels(this.chart, newLines, { ...labelSize, fontWeight: 500 }, 50);
    return { lines: newLines.concat(ticks), serie };
  };

  componentDidMount = () => {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    let that = this;
    chartConfig.tooltip.formatter = function() {
      return that.tooltipFormatter(this, that);
    };
    chartConfig.plotOptions.series.point.events.click = function() {
      that.onPointClick(that, this);
    };

    chartConfig.exporting = this.props.chartExporting;
    chartConfig.chart.events.redraw = this.onRedraw;
    chartConfig.chart.events.render = function() {
      that.selectionHalo = chartUtils.drawSelectionHalo(that.chart, that.selectionHalo);
    };

    chartConfig.exporting.menuItemDefinitions = {
      'downloadPNG': {
        text: 'Download PNG',
        onclick: this.exportChart('image/png')
      },
      'downloadJPEG': {
        text: 'Download JPEG',
        onclick: this.exportChart('image/jpeg')
      },
      'downloadPDF': {
        text: 'Download PDF',
        onclick: this.exportChart('application/pdf')
      }
    };

    this.chart = new Highcharts['Chart'](this.chartContainer.current, chartConfig);

    if (this.props.data.series) {
      this.updateChartData();
    }

    if (this.props.wallmode) {
      this.updateChartFont();
    }
  };

  componentDidUpdate(prevProps) {
    if (prevProps.isPointSelected && !this.props.isPointSelected && this.chart) {
      this.chart.getSelectedPoints().forEach(point => point.select(false, true));
      this.selectionHalo = chartUtils.drawSelectionHalo(this.chart, this.selectionHalo);
    }
    if (this.props.data.series && prevProps.data != this.props.data) {
      this.updateChartData(prevProps.data);
    }

    if (this.props.data.percentiles && prevProps.percentiles != this.props.percentiles) {
      this.setPercentiles();
    }

    if (prevProps.chartExporting != this.props.chartExporting) {
      this.chart.update(
        {
          exporting: {
            filename: this.props.chartExporting.filename,
            chartOptions: {
              title: {
                text: 'Aging Chart',
                style: {
                  fontSize: '16px',
                  fontWeight: 'bold'
                }
              },
            }
          }
        },
        false
      );
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  setPercentiles() {
    let percentiles = this.calculatePercentiles(this.props.data, this.props.data.categories.length);
    this.chart.update({
      yAxis: {
        plotLines: percentiles.lines
      }
    });
  }

  updateChartFont() {
    const newConfig = highchartUtils.getFontSizeConfig('cta');
    this.chart.update({
      ...newConfig.chart,
      // the TV's larger axis labels take the room they need
      chart: { marginLeft: null }
    });
  }

  defaultDots(series) {
    return _.map(series, serie => (serie.color === colorMap('default') ? { ...serie, marker: { ...DEFAULT_DOT_MARKER, ...serie.marker } } : serie));
  }

  updateChartData(oldData = {}) {
    let percentiles = this.calculatePercentiles(this.props.data, this.props.data.categories.length);
    const counts = this.props.data.cardsPerCategory || [];
    const timeUnit = this.props.timeUnit;
    this.chart.update(
      {
        yAxis: {
          plotLines: percentiles.lines,
          labels: {
            formatter: function() {
              return chartUtils.withUnit(this.value, timeUnit);
            }
          }
        },
        xAxis: [
          {
            ...bottomXAxisConfig,
            labels: {
              ...bottomXAxisConfig.labels,
              formatter() {
                const count = counts[this.pos];
                const name = escapeHTML(String(this.value));
                return count === undefined ? name : `${name}<br/><span style="color:#545D66;font-size:${COUNT_LABEL_SIZE}">${escapeHTML(String(count))}</span>`;
              }
            },
            categories: this.props.data.categories
          }
        ]
      },
      false
    );

    const diff = dataUtils.getDiff(this.defaultDots(oldData.series), this.defaultDots(this.props.data.series), 'data');
    highchartUtils.updateSeries(diff, this.chart);

    //Render percentiles
    const percentilesIndex = _.findIndex(this.chart.series, {
      name: 'percentiles'
    });
    if (percentilesIndex > -1) {
      this.chart.series[percentilesIndex].remove(false);
    }
    this.chart.addSeries(percentiles.serie, false);
    this.chart.redraw();

  }

  tooltipFormatter(chartScope, componentScope) {
    if (chartScope.key == 'Plotline') {
      return `${chartScope.point.text}: ${chartScope.y} ${componentScope.props.timeUnit}s`;
    }

    const cycleTimeInMinutes = chartScope.point.cycleTimeInMinutes;
    let duration;
    if (cycleTimeInMinutes !== undefined) {
      duration = formatWorkDuration(
        cycleTimeInMinutes,
        componentScope.props.excludeWeekendOpts,
        componentScope.props.timePrecision,
        componentScope.props.useWorkingHoursInCycleTimeDays
      );
    } else {
      duration = `${chartScope.y} ${componentScope.props.timeUnit}${chartScope.y != 1 ? 's' : ''}`;
    }
    let tooltip = `<p class="tooltip-label-size" style="margin: 8px 8px 0;">Cycle time: ${duration}</p>`;
    const showBlockerInfo = componentScope.props.blockers !== false;

    for (let s = 0; s < componentScope.chart.series.length; s++) {
      for (let i = 0; i < componentScope.chart.series[s].data.length; i++) {
        let point = componentScope.chart.series[s].data[i];
        if (point.card && point.x == chartScope.point.x && point.y === chartScope.y) {
          const border = showBlockerInfo && !point.card.isBlocked && !!point.card.blockedTime ? ' border: 2px solid #ec2132;' : '';
          const isBgWhite = isWhite(point.color) ? 'border: 1px solid #00408026;' : '';
          tooltip += `<div class="tooltip-list-item"><div class="tooltip-circle-dot" style="${border} ${isBgWhite} background: ${point.color}"></div>${escapeHTML(point.card.name)}</div>`;
        }
      }
    }

    tooltip += `<p class="tooltip-info-txt">Click on the dot to see the details</p>`;

    return tooltip;
  }

  onPointClick(comp, ev) {
    chartUtils.selectOnly(comp.chart, ev);
    comp.selectionHalo = chartUtils.drawSelectionHalo(comp.chart, comp.selectionHalo);
    const points = [];

    for (let s = 0; s < comp.chart.series.length; s++) {
      for (let i = 0; i < comp.chart.series[s].data.length; i++) {
        const point = comp.chart.series[s].data[i];
        if (
          point.card &&
          point.x == ev.x &&
          moment.duration(point.y, 'day').format('d') == moment.duration(ev.y, 'day').format('d')
        ) {
          points.push({
            card: point.card,
            details: point.details,
            color: point.color,
            date: point.date
          });
        }
      }
    }

    const cycleTimeInMinutes = ev.cycleTimeInMinutes !== undefined
      ? ev.cycleTimeInMinutes
      : (points.length > 0 ? _.sumBy(points[0].details, 'cycleTime') : 0);
    const duration = formatWorkDuration(cycleTimeInMinutes, comp.props.excludeWeekendOpts, comp.props.timePrecision, comp.props.useWorkingHoursInCycleTimeDays);

    comp.props.onPointClick({
      duration,
      points
    });
  }

  onRedraw(ev) {
    if (
      ev.target.oldChartWidth !== ev.target.chartWidth ||
      ev.target.oldChartHeight !== ev.target.chartHeight ||
      ev.target.axes.some(axis => axis.oldMax !== axis.max || axis.oldMin !== axis.min)
    ) {
      destroyHealthZones(this.zones);
      this.zones = renderHealthZones(this.chart, this.props.data.healthZones, this.props.allHealthZones, !this.props.wallmode);
    }
  }

  exportChart(type) {
    return () => {
      const { healthZones } = this.props.data;
      const { allHealthZones } = this.props;
      exportComposition(this.chart, type, {
        chart: {
          events: {
            redraw: function (e) {
              renderHealthZones(this, healthZones, allHealthZones);
            },
            load: function (e) {
              e.target.options.exporting.sourceHeight = e.target.chartHeight;
              e.target.options.exporting.sourceWidth = e.target.chartWidth;
              renderHealthZones(this, healthZones, allHealthZones);
            }
          }
        }
      });
      trackChartExport('cta');
    }
  }

  render() {
    return (
      <div className="chart_content chart_tooltip_style">
        <div ref={this.chartContainer} className="chart_details chart_details-full"></div>
      </div>
    );
  }
}

function destroyHealthZones(zones = []) {
  while (zones.length > 0) {
    const zone = zones.pop();
    zone.destroy();
  }
}

function renderHealthZones(chart, healthZones, allHealthZones, asLanes) {
  if (asLanes) {
    return renderZoneLanes(chart, healthZones, allHealthZones);
  }
  return renderZoneFills(chart, healthZones, allHealthZones);
}

const LANE_RADIUS = 8;
// 11px under the page's 12px state names; em so the TV's larger names keep the same step
const COUNT_LABEL_SIZE = `${11 / 12}em`;

function renderZoneLanes(chart, healthZones, allHealthZones) {
  const zones = [];
  const yMax = chart.yAxis[0].max;
  const yMin = chart.yAxis[0].min;
  const width = chart.xAxis[0].toPixels(1, false) - chart.xAxis[0].toPixels(0, false);
  const height = chart.yAxis[0].toPixels(0, false) - chart.yAxis[0].toPixels(1, false);
  const bottomEdge = chart.plotTop + chart.plotHeight;
  const gap = 4;

  _.forEach(healthZones, hz => {
    const points = _.sortBy(hz, '-y');
    const bands = [];
    let x = 0;
    let w = 0;

    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      const next = points[i + 1];

      x = chart.xAxis[0].toPixels(p.x, false) - width / 2;
      w = width;
      if (x + w > chart.plotLeft + chart.plotWidth) {
        w = chart.plotLeft + chart.plotWidth - x;
      }
      if (x < chart.plotLeft) {
        w = w + x - chart.plotLeft;
        x = chart.plotLeft;
      }

      if (!i && p.y < yMax) {
        bands.push({
          fill: ZONE_OVERFLOW_FILL,
          y: chart.plotTop,
          h: (yMax - Math.max(yMin, p.y)) * height
        });
      }

      let y = chart.yAxis[0].toPixels(p.y, false);
      let h = next ? (p.y - next.y) * height : (p.y - yMin) * height;
      if (y + h > bottomEdge) {
        h = bottomEdge - y;
      }
      if (y < chart.plotTop) {
        h = h + y - chart.plotTop;
        y = chart.plotTop;
      }
      bands.push({ fill: _.find(allHealthZones, { value: p.percentile }).color, y, h });
    }

    const visible = bands.filter(band => band.h > 0);
    if (w - 2 * gap <= 0) {
      return;
    }
    if (!visible.length) {
      return;
    }
    // rounding each band's own corners squared the column off where its first or last band was thinner than the corner
    const laneTop = visible[0].y;
    const laneHeight = _.last(visible).y + _.last(visible).h - laneTop;
    const laneWidth = w - 2 * gap;
    const outline = chart.renderer.clipRect(x + gap, laneTop, laneWidth, laneHeight);
    outline.attr({ r: Math.min(LANE_RADIUS, laneHeight / 2, laneWidth / 2) });
    zones.push(outline);
    visible.forEach(band => {
      zones.push(
        chart.renderer
          .rect(x + gap, band.y, laneWidth, band.h)
          .attr({ fill: band.fill, zIndex: 0, class: 'aging-zone' })
          .clip(outline)
          .add()
      );
    });
  });

  return zones;
}

function renderZoneFills(chart, healthZones, allHealthZones) {
  const zones = [];
  let yMax = chart.yAxis[0].max;
  let yMin = chart.yAxis[0].min;
  let width = chart.xAxis[0].toPixels(1, false) - chart.xAxis[0].toPixels(0, false);
  let height = chart.yAxis[0].toPixels(0, false) - chart.yAxis[0].toPixels(1, false);

  _.forEach(healthZones, hz => {
    const points = _.sortBy(hz, '-y');

    let w, h, p, offsetX, offsetY, next;

    const z = [];

    for (let i = 0; i < points.length; i++) {

      p = points[i];
      next = points[i + 1];

      offsetX = chart.xAxis[0].toPixels(p.x, false) - width / 2;
      w = width;
      if (offsetX + w > chart.plotLeft + chart.plotWidth) {
        w = chart.plotLeft + chart.plotWidth - offsetX;
      }
      if (offsetX < chart.plotLeft) {
        w = w + offsetX - chart.plotLeft;
        offsetX = chart.plotLeft;
      }

      if (!i && p.y < yMax) {
        h = (yMax - Math.max(yMin, p.y)) * height;
        offsetY = chart.plotTop;
        z.push({
          fill: ZONE_OVERFLOW_FILL,
          offsetX,
          offsetY,
          w,
          h,
        });
      }

      h = next ? (p.y - next.y) * height : (p.y - yMin) * height;
      offsetY = chart.yAxis[0].toPixels(p.y, false);
      if (offsetY + h > chart.plotTop + chart.plotHeight) {
        h = chart.plotTop + chart.plotHeight - offsetY;
      }
      if (offsetY < chart.plotTop) {
        h = h + offsetY - chart.plotTop;
        offsetY = chart.plotTop;
      }

      z.push({
        fill: _.find(allHealthZones, { value: p.percentile }).color,
        offsetX,
        offsetY,
        w,
        h,
      });
    }

    _.forEach(z, ({ w, h, offsetX, offsetY, fill }) => {
      if (w > 0 && h > 0) {
        zones
          .push(
            chart.renderer
            .rect(offsetX, offsetY, w, h, 0)
            .attr({ fill, zIndex: 0 })
            .add()
          );
      }
    });
  });

  return zones;
}

const mapStateToProps = state => {
  return {
    allHealthZones: state.filters.allHealthZones,
    dateFormat: state.filters.dateFormat,
    percentiles: state.filters.cta.selectedPercentiles,
    selectedHealthZones: state.filters.cta.selectedHealthZones,
    timeUnit: state.filters.cta.timeUnit,
    wallmode: state.app.wallmode,
    timePrecision: state.filters.timePrecision,
    excludeWeekendOpts: {
      type: state.filters.excludeWeekends,
      workStart: state.filters.workStart,
      workEnd: state.filters.workEnd,
      holidays: state.filters.holidays
    },
    useWorkingHoursInCycleTimeDays: state.auth.user.config && state.auth.user.config.useWorkingHoursInCycleTimeDays
  };
};

export default withChartExport(connect(mapStateToProps)(CTAChart));
